"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ChartScreen from "@/components/chart/ChartScreen";
import { parseChartPopupTimeframe } from "@/lib/chart-popup/openChartPopup";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";

function ChartPopupContent() {
  const [searchParams] = useSearchParams();
  const symbolParam = searchParams.get("symbol");
  const timeframeParam = searchParams.get("timeframe");
  const allSubscribed = useSubscribedSymbolsStore((s) => s.allSubscribed);
  // Subscribe to the symbols store's byCurrency map so this effect re-runs when
  // that store hydrates. Previously seeding only retried on allSubscribed
  // changes, so a symbol that lives only in useSymbolsStore (read imperatively)
  // could silently never seed if that store hydrated after mount.
  const symbolsByCurrency = useSymbolsStore((s) => s.byCurrency);
  const seededRef = useRef(false);
  const [seeded, setSeeded] = useState(false);

  // A missing/blank symbol param has nothing to seed — never block on it.
  const hasSymbolParam = Boolean(symbolParam?.trim());

  useEffect(() => {
    if (seededRef.current || !symbolParam?.trim()) {
      return;
    }

    const currency = symbolParam.trim();
    const fromSubscribed = allSubscribed.find(
      (entry) => entry.symbol.currency === currency
    )?.symbol;
    const resolved =
      fromSubscribed ?? symbolsByCurrency.get(currency);
    if (!resolved) {
      return;
    }

    seededRef.current = true;
    useChartStore3.getState().setSelectedSymbol(resolved, 0);
    const tf = parseChartPopupTimeframe(timeframeParam);
    if (tf) {
      useChartStore3.getState().setSelectedTimeframe(tf, 0);
    }
    setSeeded(true);
  }, [symbolParam, timeframeParam, allSubscribed, symbolsByCurrency]);

  // Show the loading state until the requested symbol has been seeded, so the
  // chart never renders an empty/wrong symbol while stores are still hydrating.
  if (hasSymbolParam && !seeded) {
    return <ChartPopupFallback />;
  }

  return (
    <div className="h-dvh w-full max-w-full overflow-hidden bg-background text-foreground pt-[env(safe-area-inset-top,0px)]">
      <ChartScreen isDesktop={true} isPopup={true} />
    </div>
  );
}

function ChartPopupFallback() {
  return (
    <div className="flex h-dvh w-full max-w-full items-center justify-center bg-background">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-foreground" />
    </div>
  );
}

export default function ChartPopupPage() {
  return (
    <Suspense fallback={<ChartPopupFallback />}>
      <ChartPopupContent />
    </Suspense>
  );
}
